import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import TenantDataAreaService from "../../services/tenant/TenantDataAreaService";
import { faDatabase } from "@fortawesome/free-solid-svg-icons";
import CrudManager from "../../components/common/CrudManager";
import ImageUploaderWithCrop from "../../components/utils/ImageUploaderWithCrop";
import ColorPicker from "../../components/utils/ColorPicker";
import Alert from "../../components/common/Alert";
import { getImageUrl } from "../../utils/imageUrlHelper";

const DEFAULT_BRANDING = {
  displayName: "",
  logoImage: null,
  faviconImage: null,
  primaryColor: "#159f91",
  fontPrimaryColor: "#FFFFFF",
  headerFooterColor: "#07111e",
  fontHeaderFooterColor: "#e9f2fb",
  enabled: true,
};

const base64ToBlob = async (base64) => {
  const response = await fetch(base64);
  return await response.blob();
};

const buildBrandingFormData = async (branding) => {
  const formData = new FormData();
  formData.set("displayName", branding.displayName || "");
  formData.set("primaryColor", branding.primaryColor || "");
  formData.set("fontPrimaryColor", branding.fontPrimaryColor || "");
  formData.set("headerFooterColor", branding.headerFooterColor || "");
  formData.set("fontHeaderFooterColor", branding.fontHeaderFooterColor || "");
  formData.set("enabled", String(branding.enabled !== false));

  if (branding.logoImage && typeof branding.logoImage === "string" && branding.logoImage.startsWith("data:image/")) {
    const blob = await base64ToBlob(branding.logoImage);
    formData.set("logoImage", blob, "logo.png");
  }

  if (branding.faviconImage && typeof branding.faviconImage === "string" && branding.faviconImage.startsWith("data:image/")) {
    const blob = await base64ToBlob(branding.faviconImage);
    formData.set("faviconImage", blob, "favicon.png");
  }

  return formData;
};

const dispatchBrandingUpdate = (branding) => {
  window.dispatchEvent(new CustomEvent("themeUpdated", {
    detail: {
      primaryColor: branding?.primaryColor || DEFAULT_BRANDING.primaryColor,
      fontPrimaryColor: branding?.fontPrimaryColor || DEFAULT_BRANDING.fontPrimaryColor,
      headerFooterColor: branding?.headerFooterColor || DEFAULT_BRANDING.headerFooterColor,
      fontHeaderFooterColor: branding?.fontHeaderFooterColor || DEFAULT_BRANDING.fontHeaderFooterColor,
    },
  }));
  window.dispatchEvent(new CustomEvent("logoUpdated", {
    detail: { logoUrl: branding?.logoImage || null },
  }));
  window.dispatchEvent(new CustomEvent("faviconUpdated", {
    detail: { faviconUrl: branding?.faviconImage || null },
  }));
};

const TenantDataAreaForm = ({ formData, onChange, isEditing, tenantId, service, onBrandingChange }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("general");
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [isLoadingBranding, setIsLoadingBranding] = useState(false);
  const [brandingError, setBrandingError] = useState("");
  const [isEditingLogo, setIsEditingLogo] = useState(false);
  const [isEditingFavicon, setIsEditingFavicon] = useState(false);

  const dataAreaId = formData?.dataAreaId || formData?.id;

  useEffect(() => {
    const loadBranding = async () => {
      if (!isEditing || !tenantId || !dataAreaId) {
        setBranding(DEFAULT_BRANDING);
        setIsEditingLogo(false);
        setIsEditingFavicon(false);
        return;
      }

      setIsLoadingBranding(true);
      setBrandingError("");
      try {
        const response = await service.getBranding(dataAreaId);
        setBranding({
          ...DEFAULT_BRANDING,
          ...response,
          logoImage: response?.logoImage || null,
          faviconImage: response?.faviconImage || null,
        });
        setIsEditingLogo(false);
        setIsEditingFavicon(false);
      } catch (error) {
        setBrandingError(error.message || t("common.errorOccurred"));
      } finally {
        setIsLoadingBranding(false);
      }
    };

    loadBranding();
  }, [dataAreaId, isEditing, service, tenantId, t]);

  useEffect(() => {
    onBrandingChange(branding);
  }, [branding, onBrandingChange]);

  const updateField = (field, value) => {
    onChange({
      ...formData,
      [field]: value,
    });
  };

  const updateBrandingField = (field, value) => {
    setBranding((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="tabs">
      <ul className="nav nav-tabs">
        <li className={activeTab === "general" ? "active" : ""}>
          <a href="#general" onClick={(event) => { event.preventDefault(); setActiveTab("general"); }}>
            {t("dataAreaBranding.tabs.general")}
          </a>
        </li>
        <li className={activeTab === "branding" ? "active" : ""}>
          <a href="#branding" onClick={(event) => { event.preventDefault(); setActiveTab("branding"); }}>
            {t("dataAreaBranding.tabs.branding")}
          </a>
        </li>
      </ul>

      <div className="tab-content">
        {activeTab === "general" && (
          <div className="tab-pane active">
            <div className="form-group">
              <label className="control-label">{t("dataAreas.dataAreaId")}</label>
              <input
                type="text"
                className="form-control"
                maxLength={3}
                value={formData.dataAreaId || ""}
                disabled={isEditing}
                onChange={(event) => updateField("dataAreaId", event.target.value.toUpperCase())}
              />
            </div>
            <div className="form-group">
              <label className="control-label">{t("dataAreas.companyId")}</label>
              <input
                type="number"
                className="form-control"
                value={formData.companyId || ""}
                onChange={(event) => updateField("companyId", event.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="control-label">
                <input
                  type="checkbox"
                  checked={formData.enabled !== false}
                  onChange={(event) => updateField("enabled", event.target.checked)}
                  style={{ marginRight: 8 }}
                />
                {t("dataAreas.enabled")}
              </label>
            </div>
          </div>
        )}

        {activeTab === "branding" && (
          <div className="tab-pane active">
            {!isEditing && (
              <Alert type="info" message={t("dataAreaBranding.saveDataAreaFirst")} />
            )}
            {brandingError && (
              <Alert type="error" message={brandingError} onClose={() => setBrandingError("")} />
            )}
            {isLoadingBranding && <div className="text-muted">{t("dataAreaBranding.loading")}</div>}
            <div className="form-group">
              <label className="control-label">{t("dataAreaBranding.displayName")}</label>
              <input
                type="text"
                className="form-control"
                disabled={!isEditing}
                value={branding.displayName || ""}
                onChange={(event) => updateBrandingField("displayName", event.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="control-label">{t("dataAreaBranding.logo")}</label>
              {!isEditingLogo && branding.logoImage && typeof branding.logoImage === "string" && !branding.logoImage.startsWith("data:image/") ? (
                <div className="image-preview-block">
                  <img
                    src={getImageUrl(branding.logoImage)}
                    alt={t("dataAreaBranding.logo")}
                    className="preview-image-modal"
                  />
                  <button
                    type="button"
                    className="btn btn-secondary image-preview-action"
                    disabled={!isEditing}
                    onClick={() => setIsEditingLogo(true)}
                  >
                    {t("common.changeImage")}
                  </button>
                </div>
              ) : (
                isEditing && (
                  <ImageUploaderWithCrop
                    onImageChange={(image) => {
                      updateBrandingField("logoImage", image);
                      setIsEditingLogo(true);
                    }}
                  />
                )
              )}
            </div>
            <div className="form-group">
              <label className="control-label">{t("dataAreaBranding.favicon")}</label>
              <p className="help-block">{t("dataAreaBranding.faviconHelp")}</p>
              {!isEditingFavicon && branding.faviconImage && typeof branding.faviconImage === "string" && !branding.faviconImage.startsWith("data:image/") ? (
                <div className="image-preview-block">
                  <img
                    src={getImageUrl(branding.faviconImage)}
                    alt={t("dataAreaBranding.favicon")}
                    className="preview-image-modal"
                    style={{ width: 64, height: 64, objectFit: "contain" }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary image-preview-action"
                    disabled={!isEditing}
                    onClick={() => setIsEditingFavicon(true)}
                  >
                    {t("common.changeImage")}
                  </button>
                </div>
              ) : (
                isEditing && (
                  <ImageUploaderWithCrop
                    aspectRatio={1}
                    maxWidth={512}
                    maxHeight={512}
                    onImageChange={(image) => {
                      updateBrandingField("faviconImage", image);
                      setIsEditingFavicon(true);
                    }}
                  />
                )
              )}
            </div>
            <div className="form-group">
              <label className="control-label">{t("dataAreaBranding.primaryColor")}</label>
              <ColorPicker
                value={branding.primaryColor || DEFAULT_BRANDING.primaryColor}
                onColorChange={(color) => updateBrandingField("primaryColor", color)}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label className="control-label">{t("dataAreaBranding.fontPrimaryColor")}</label>
              <ColorPicker
                value={branding.fontPrimaryColor || DEFAULT_BRANDING.fontPrimaryColor}
                onColorChange={(color) => updateBrandingField("fontPrimaryColor", color)}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label className="control-label">{t("dataAreaBranding.headerFooterColor")}</label>
              <ColorPicker
                value={branding.headerFooterColor || DEFAULT_BRANDING.headerFooterColor}
                onColorChange={(color) => updateBrandingField("headerFooterColor", color)}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label className="control-label">{t("dataAreaBranding.fontHeaderFooterColor")}</label>
              <ColorPicker
                value={branding.fontHeaderFooterColor || DEFAULT_BRANDING.fontHeaderFooterColor}
                onColorChange={(color) => updateBrandingField("fontHeaderFooterColor", color)}
                disabled={!isEditing}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TenantDataAreas = () => {
  const pendingBrandingRef = useRef(DEFAULT_BRANDING);
  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const isSuperuser = userData?.superuser === true;

  // Use user.tenantId for non-superusers, or selectedTenant for superusers
  const selectedTenant = localStorage.getItem("selectedTenant");
  const tenantId = userData?.tenantId || selectedTenant;

  // Define columns - flat structure
  // Note: 'id' column is required by CrudManager - it maps to 'dataAreaId' from the backend
  const columns = [
    {
      key: "id",
      // Hidden column - used internally for CRUD operations
      showInGrid: false,
      showInForm: false,
    },
    {
      key: "dataAreaId",
      label: "dataAreas.dataAreaId",
      sortable: true,
      showInFilter: true,
      type: "text",
      // Form:
      showInForm: true,
      placeholder: "dataAreas.dataAreaIdPlaceholder",
      required: true,
    },
    {
      key: "enabled",
      label: "dataAreas.enabled",
      sortable: true,
      showInFilter: true,
      type: "boolean",
      render: (row) => (row.enabled ? "✓" : "✗"),
    },
  ];

  // If no tenantId, show message (only for superusers)
  if (!tenantId) {
    if (isSuperuser) {
      // Create service instance even with null tenantId (will show error from API)
      const service = new TenantDataAreaService(null);
      return (
        <CrudManager
          service={service}
          columns={columns}
          entityName="dataAreas"
          permissionPrefix="system.dataareas"
          icon={faDatabase}
          formLayout="modal"
          noDataMessage="dataAreas.errors.noTenant"
        />
      );
    }
    return null;
  }

  // Create service instance with tenantId
  const service = new TenantDataAreaService(tenantId);

  const handleBeforeUpdate = async (formData, editId) => {
    const dataAreaPayload = {
      companyId: formData.companyId === "" ? null : formData.companyId,
      enabled: formData.enabled !== false,
    };

    const branding = pendingBrandingRef.current;
    if (branding) {
      const brandingFormData = await buildBrandingFormData(branding);
      const updatedBranding = await service.updateBranding(editId, brandingFormData);
      const currentDataAreaId = localStorage.getItem("dataAreaId");
      if (String(currentDataAreaId).toUpperCase() === String(editId).toUpperCase()) {
        dispatchBrandingUpdate(updatedBranding);
      }
    }

    return dataAreaPayload;
  };

  return (
    <CrudManager
      service={service}
      columns={columns}
      entityName="dataAreas"
      permissionPrefix="system.dataareas"
      icon={faDatabase}
      formLayout="modal"
      renderForm={(props) => (
        <TenantDataAreaForm
          {...props}
          tenantId={tenantId}
          service={service}
          onBrandingChange={(branding) => {
            pendingBrandingRef.current = branding;
          }}
        />
      )}
      onBeforeUpdate={handleBeforeUpdate}
    />
  );
};

export default TenantDataAreas;
